import { useEffect, useState } from "react";
import { Download, Share, Smartphone, Check } from "lucide-react";
import { toast } from "sonner";
import { canPrompt, isIOS, isStandalone, promptInstall, subscribe } from "@/lib/pwa";

// "Install ChangeOS" — native prompt where the browser offers one (Chrome/Edge/Samsung), Add-to-Home-Screen steps on iOS Safari, nothing when already installed.
export function useInstallState() {
  const [tick, setTick] = useState(0);
  useEffect(() => subscribe(() => setTick((t) => t + 1)), []);
  const standalone = isStandalone();
  return { standalone, prompt: !standalone && canPrompt(), ios: !standalone && isIOS() && !canPrompt(), tick };
}

export default function InstallApp({ variant = "card" }) {
  const st = useInstallState();
  const [showIos, setShowIos] = useState(false);
  if (st.standalone) return variant === "card" ? (
    <section className="pp-card p-6 mt-5" data-testid="install-app"><h2 className="font-head text-lg font-extrabold flex items-center gap-2"><Check size={16} className="text-success-600" /> Installed on this device</h2><p className="text-sm text-subtle mt-2">You're running ChangeOS as an app. It opens straight to your dashboard and sits with your other apps.</p></section>
  ) : null;
  if (!st.prompt && !st.ios) return variant === "card" ? (
    <section className="pp-card p-6 mt-5" data-testid="install-app"><h2 className="font-head text-lg font-extrabold flex items-center gap-2"><Smartphone size={16} /> Install on your phone or desktop</h2><p className="text-sm text-subtle mt-2">ChangeOS installs like an app from Chrome, Edge or Safari. Open this page on your phone and use the browser's <span className="font-semibold text-foreground">Install app</span> / <span className="font-semibold text-foreground">Add to Home Screen</span> option — it will appear here as a button once your browser offers it.</p></section>
  ) : null;
  const go = async () => {
    if (st.ios) { setShowIos((v) => !v); return; }
    const r = await promptInstall();
    if (r === "accepted") toast.success("ChangeOS installed — find it with your other apps.");
  };
  const IosSteps = () => showIos ? (
    <ol className="mt-3 space-y-1.5 text-sm text-subtle list-decimal pl-5" data-testid="install-ios-steps">
      <li>Tap the <span className="font-semibold text-foreground inline-flex items-center gap-1">Share <Share size={12} /></span> button in Safari's toolbar.</li>
      <li>Scroll and choose <span className="font-semibold text-foreground">Add to Home Screen</span>.</li>
      <li>Tap <span className="font-semibold text-foreground">Add</span> — ChangeOS opens full-screen from your home screen.</li>
    </ol>
  ) : null;
  if (variant === "sidebar") return (
    <div className="px-3 pb-2">
      <button type="button" data-testid="install-app-sidebar" onClick={go} className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-ink-border text-on-dark/90 hover:bg-ink-soft text-xs font-bold py-2 transition-colors"><Download size={13} /> Install ChangeOS</button>
      {st.ios && showIos && <div className="mt-2 text-[11px] text-muted-foreground" data-testid="install-ios-steps">Safari: tap Share, then “Add to Home Screen”.</div>}
    </div>
  );
  return (
    <section className="pp-card p-6 mt-5" data-testid="install-app">
      <h2 className="font-head text-lg font-extrabold flex items-center gap-2"><Smartphone size={16} /> Install on this device</h2>
      <p className="text-sm text-subtle mt-2">Get ChangeOS on your home screen: opens full-screen, straight to your dashboard, no browser bar. Nothing to download from an app store.</p>
      <button type="button" data-testid="install-app-btn" onClick={go} className="pp-btn-dark mt-4 text-sm"><Download size={14} /> {st.ios ? "How to add to Home Screen" : "Install ChangeOS"}</button>
      <IosSteps />
    </section>
  );
}
